import type { Permissions } from '@/context/auth/types';

import {
  ORDER_CREATE_PERMISSION_PREFIXES,
  ORDER_UPDATE_PERMISSION_PREFIXES,
} from '@/constants/permissions';
import {
  canAccessOrderPayloadLinks,
  canCreateOrder,
  canDownloadOrderPayload,
  canUpdateOrder,
  canViewDeliveryStatus,
  hasAnyPermission,
  hasAnyPermissionPrefix,
  hasPermission,
} from '@/context/auth/permissionUtils';
import { useUserProfile } from '@/hooks/useUsers';

export const useHasPermission = ({
  name,
}: {
  name?: Permissions;
}) => {
  const { organizationPermissions = [] } = useUserProfile();
  return hasPermission(organizationPermissions, name);
};

export const useHasAnyPermission = (names: Permissions[]) => {
  const { organizationPermissions = [] } = useUserProfile();
  return hasAnyPermission(organizationPermissions, names);
};

export const useHasAnyPermissionPrefix = (prefixes: Permissions[]) => {
  const { organizationPermissions = [] } = useUserProfile();
  return hasAnyPermissionPrefix(organizationPermissions, prefixes);
};

export const useCanCreateOrder = () => {
  const { organizationPermissions = [] } = useUserProfile();
  return canCreateOrder(organizationPermissions);
};

export const useCanUpdateOrder = () => {
  const { organizationPermissions = [] } = useUserProfile();
  return canUpdateOrder(organizationPermissions);
};

export const useCanDownloadOrderPayload = (productCategory?: string) => {
  const { organizationPermissions = [] } = useUserProfile();
  return canDownloadOrderPayload(organizationPermissions, productCategory);
};

export const useCanViewDeliveryStatus = (productCategory?: string) => {
  const { organizationPermissions = [] } = useUserProfile();
  return canViewDeliveryStatus(organizationPermissions, productCategory);
};

export const useCanAccessOrderPayloadLinks = (productCategory?: string) => {
  const { organizationCustomerType, organizationPermissions = [] } = useUserProfile();
  return canAccessOrderPayloadLinks({
    organizationCustomerType,
    organizationPermissions,
    productCategory,
  });
};

export const ORDER_PERMISSION_PREFIXES = {
  CREATE: ORDER_CREATE_PERMISSION_PREFIXES,
  UPDATE: ORDER_UPDATE_PERMISSION_PREFIXES,
};
